import type { StatementPeriod } from '../types/dealerFundStatementPeriod.types';
import { isUuid } from '../utils/isUuid';
import { utcTodayIsoDate } from '../utils/statementPeriod';
import { payloadReaders } from './statementPayloadReaders';

const { malformed, readText, readNullableText } = payloadReaders('fund statement period payload');

export const STATEMENT_LINE_KEY = /^[A-Z0-9]{1,20}(_REFUND)?$/;

export const readBoundedNullable = (value: unknown, label: string, max: number): string | null => {
    const text = readNullableText(value, label);
    if (text !== null && text.length > max) throw malformed(`${label} is longer than ${max} characters`);
    return text;
};

export const readBoundedText = (value: unknown, label: string, max: number): string => {
    const text = readText(value, label);
    if (text.length > max) throw malformed(`${label} is longer than ${max} characters`);
    return text;
};

export const readUuid = (value: unknown, label: string): string => {
    const text = readText(value, label);
    if (!isUuid(text)) throw malformed(`${label} is not a valid id`);
    return text;
};

export const readMatchingId = (value: unknown, label: string, expected: string): string => {
    const id = readUuid(value, label);
    if (id.toLowerCase() !== expected.toLowerCase()) throw malformed(`${label} does not match the request`);
    return id;
};

export const monthWindowEnd = (month: string, todayIso: string): string => {
    const [year, number] = month.split('-').map(Number);
    const lastDay = new Date(Date.UTC(year, number, 0)).getUTCDate();
    const monthEnd = `${month}-${String(lastDay).padStart(2, '0')}`;
    return monthEnd < todayIso ? monthEnd : todayIso;
};

export const assertWindow = (period: StatementPeriod, from: string, to: string): void => {
    if (from > to) throw malformed('window is reversed');
    if (period.kind === 'range') {
        if (from !== period.from || to !== period.to) throw malformed('window does not match the request');
        return;
    }
    const expectedTo = monthWindowEnd(period.month, utcTodayIsoDate(new Date()));
    if (from !== `${period.month}-01` || to !== expectedTo) {
        throw malformed('window does not match the request');
    }
};
